import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bed, Users, Bath, Maximize } from "lucide-react";
import { api, PROPERTY_IMAGES } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/lib/useSEO";

const RoomsPage = () => {
  const [property, setProperty] = useState(null);
  const [hovered, setHovered] = useState(null);
  const { t, lang } = useLang();

  useSEO({
    title: lang === "en" ? "Rooms & Floor Plan" : "Stanze e planimetria",
    description: lang === "en"
      ? "Discover the real floor plan of La dimora di Patty: two independent bedrooms, spacious living area with kitchen, and bathroom with shower. 65 sqm of mountain elegance."
      : "Scopri la planimetria reale della dimora: due camere indipendenti, ampia zona giorno con cucina, bagno con doccia. 65 mq di eleganza di montagna a Castel di Sangro.",
  });

  useEffect(() => {
    api.get("/property").then((r) => setProperty(r.data));
  }, []);

  const ROOMS = [
    {
      id: "camera",
      name: "Camera Matrimoniale",
      desc: "Camera elegante con letto matrimoniale, parete in resina effetto cemento, ampio armadio a specchio e dettagli artistici.",
      img: PROPERTY_IMAGES.camera,
      beds: "1 letto matrimoniale",
      guests: 2,
    },
    {
      id: "cameretta",
      name: "Cameretta",
      desc: "Camera con letto a castello, perfetta per famiglie con bambini o coppie di amici. TV e luce naturale generosa.",
      img: PROPERTY_IMAGES.cameretta,
      beds: "1 letto a castello",
      guests: 2,
    },
    {
      id: "cucina",
      name: "Cucina · Soggiorno",
      desc: "Cucina completamente attrezzata aperta sul soggiorno. Tavolo da pranzo, divano, TV e ambiente luminoso.",
      img: PROPERTY_IMAGES.cucinaAlt,
      beds: "Zona giorno",
      guests: 4,
    },
    {
      id: "bagno",
      name: "Bagno",
      desc: "Bagno raffinato con box doccia ampio, sanitari sospesi, doppia mobiletteria contenitiva, scaldasalviette e set di cortesia.",
      img: PROPERTY_IMAGES.bagnoAlt,
      beds: "Doccia & lavabo",
      guests: null,
    },
    {
      id: "esterno",
      name: "L'edificio",
      desc: "La palazzina che ospita la dimora, nel cuore di Castel di Sangro. Posizione strategica a pochi passi dal centro storico e dai collegamenti per Roccaraso.",
      img: PROPERTY_IMAGES.esterno,
      beds: "Esterno",
      guests: null,
    },
  ];

  return (
    <main data-testid="rooms-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="text-center mb-16">
          <div className="overline text-accent gold-line-center mb-6">{t("rooms.page_overline")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-tight">
            {t("rooms.page_title")}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto mt-6">
            {t("rooms.page_subtitle")}
          </p>
        </Reveal>

        {/* Interactive Floor Plan */}
        <Reveal className="mb-20">
          <div className="bg-card border border-border rounded-3xl p-8 md:p-12">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="relative">
                <svg viewBox="0 0 540 380" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
                  {/* outer apartment perimeter */}
                  <rect x="20" y="20" width="500" height="290" fill="hsl(var(--background))" stroke="hsl(var(--accent))" strokeWidth="3" />

                  {/* Camera matrimoniale — 3.6 x 2.8 */}
                  <rect
                    x="20" y="20" width="190" height="150"
                    fill={hovered === "camera" ? "hsl(var(--accent) / 0.28)" : "hsl(var(--accent) / 0.06)"}
                    stroke="hsl(var(--accent))" strokeWidth="1.5"
                    onMouseEnter={() => setHovered("camera")} onMouseLeave={() => setHovered(null)}
                    style={{ cursor: "pointer", transition: "fill 0.35s" }}
                  />
                  <text x="115" y="90" textAnchor="middle" fill="hsl(var(--foreground))" fontSize="13" fontFamily="Cormorant Garamond">{t("plan.camera_matr")}</text>
                  <text x="115" y="110" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="10" fontFamily="Outfit">3,6 × 2,8 m</text>
                  <text x="115" y="125" textAnchor="middle" fill="hsl(var(--accent))" fontSize="10" fontFamily="Outfit" fontWeight="500">≈ 10 mq</text>

                  {/* Cameretta — 3.4 x 2.6 */}
                  <rect
                    x="210" y="20" width="170" height="150"
                    fill={hovered === "cameretta" ? "hsl(var(--accent) / 0.28)" : "hsl(var(--accent) / 0.06)"}
                    stroke="hsl(var(--accent))" strokeWidth="1.5"
                    onMouseEnter={() => setHovered("cameretta")} onMouseLeave={() => setHovered(null)}
                    style={{ cursor: "pointer", transition: "fill 0.35s" }}
                  />
                  <text x="295" y="90" textAnchor="middle" fill="hsl(var(--foreground))" fontSize="13" fontFamily="Cormorant Garamond">{t("plan.cameretta")}</text>
                  <text x="295" y="110" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="10" fontFamily="Outfit">3,4 × 2,6 m</text>
                  <text x="295" y="125" textAnchor="middle" fill="hsl(var(--accent))" fontSize="10" fontFamily="Outfit" fontWeight="500">≈ 9 mq</text>

                  {/* Angolo cottura — 3.2 x ~2.5 (upper right) */}
                  <rect
                    x="380" y="20" width="140" height="150"
                    fill={hovered === "cucina" ? "hsl(var(--accent) / 0.28)" : "hsl(var(--accent) / 0.06)"}
                    stroke="hsl(var(--accent))" strokeWidth="1.5"
                    onMouseEnter={() => setHovered("cucina")} onMouseLeave={() => setHovered(null)}
                    style={{ cursor: "pointer", transition: "fill 0.35s" }}
                  />
                  <text x="450" y="90" textAnchor="middle" fill="hsl(var(--foreground))" fontSize="12" fontFamily="Cormorant Garamond">{t("plan.angolo")}</text>
                  <text x="450" y="106" textAnchor="middle" fill="hsl(var(--foreground))" fontSize="12" fontFamily="Cormorant Garamond">{t("plan.cottura")}</text>
                  <text x="450" y="124" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="10" fontFamily="Outfit">3,2 m</text>

                  {/* Soggiorno / Pranzo — below cameretta + below kitchen */}
                  <rect
                    x="210" y="170" width="310" height="140"
                    fill={hovered === "soggiorno" ? "hsl(var(--accent) / 0.28)" : "hsl(var(--accent) / 0.06)"}
                    stroke="hsl(var(--accent))" strokeWidth="1.5"
                    onMouseEnter={() => setHovered("soggiorno")} onMouseLeave={() => setHovered(null)}
                    style={{ cursor: "pointer", transition: "fill 0.35s" }}
                  />
                  <text x="365" y="232" textAnchor="middle" fill="hsl(var(--foreground))" fontSize="14" fontFamily="Cormorant Garamond">{t("plan.soggiorno")}</text>
                  <text x="365" y="252" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="10" fontFamily="Outfit">{t("plan.depth")}</text>
                  <text x="365" y="268" textAnchor="middle" fill="hsl(var(--accent))" fontSize="10" fontFamily="Outfit" fontWeight="500">{t("plan.living_area")}</text>

                  {/* Disimpegno — 2.9 x 1.2 */}
                  <rect
                    x="20" y="170" width="190" height="55"
                    fill={hovered === "disimpegno" ? "hsl(var(--accent) / 0.28)" : "hsl(var(--muted) / 0.4)"}
                    stroke="hsl(var(--accent))" strokeWidth="1.5"
                    onMouseEnter={() => setHovered("disimpegno")} onMouseLeave={() => setHovered(null)}
                    style={{ cursor: "pointer", transition: "fill 0.35s" }}
                  />
                  <text x="115" y="195" textAnchor="middle" fill="hsl(var(--foreground))" fontSize="11" fontFamily="Cormorant Garamond">Disimpegno</text>
                  <text x="115" y="210" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="9" fontFamily="Outfit">2,9 × 1,2 m</text>

                  {/* Bagno — 2.3 x 2.1 */}
                  <rect
                    x="20" y="225" width="190" height="85"
                    fill={hovered === "bagno" ? "hsl(var(--accent) / 0.28)" : "hsl(var(--accent) / 0.06)"}
                    stroke="hsl(var(--accent))" strokeWidth="1.5"
                    onMouseEnter={() => setHovered("bagno")} onMouseLeave={() => setHovered(null)}
                    style={{ cursor: "pointer", transition: "fill 0.35s" }}
                  />
                  <text x="115" y="262" textAnchor="middle" fill="hsl(var(--foreground))" fontSize="13" fontFamily="Cormorant Garamond">{t("plan.bagno")}</text>
                  <text x="115" y="280" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="10" fontFamily="Outfit">2,3 × 2,1 m</text>
                  <text x="115" y="295" textAnchor="middle" fill="hsl(var(--accent))" fontSize="10" fontFamily="Outfit" fontWeight="500">≈ 5 mq</text>

                  {/* Terrace / outdoor area below soggiorno */}
                  <rect x="210" y="310" width="310" height="32" fill="hsl(var(--muted) / 0.25)" stroke="hsl(var(--accent) / 0.5)" strokeWidth="1" strokeDasharray="4 3" />
                  <text x="365" y="331" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="10" fontFamily="Outfit" letterSpacing="2">{t("plan.outdoor")}</text>

                  {/* Entrance arrow */}
                  <text x="115" y="335" textAnchor="middle" fill="hsl(var(--accent))" fontSize="16">▲</text>
                  <text x="115" y="352" textAnchor="middle" fill="hsl(var(--muted-foreground))" fontSize="9" fontFamily="Outfit" letterSpacing="2">{t("plan.entrance")}</text>

                  {/* North compass */}
                  <g transform="translate(500, 350)">
                    <circle r="13" fill="none" stroke="hsl(var(--accent))" strokeWidth="1" />
                    <text x="0" y="-15" textAnchor="middle" fill="hsl(var(--accent))" fontSize="10" fontFamily="Outfit" fontWeight="600">N</text>
                    <path d="M0,-9 L4,8 L0,5 L-4,8 Z" fill="hsl(var(--accent))" />
                  </g>
                </svg>
              </div>

              <div>
                <div className="overline text-accent gold-line mb-4">{t("rooms.explore_overline")}</div>
                <h3 className="font-serif text-3xl mb-4">{t("rooms.explore_title")}</h3>
                <p className="text-muted-foreground leading-relaxed mb-3">
                  {t("rooms.explore_desc")}
                </p>
                <p className="text-xs text-muted-foreground mb-8 italic">
                  {t("rooms.explore_note")}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3"><Maximize size={18} className="text-accent" /><span className="text-sm">{t("rooms.stat_surface")}</span></div>
                  <div className="flex items-center gap-3"><Bed size={18} className="text-accent" /><span className="text-sm">{t("rooms.stat_bedrooms")}</span></div>
                  <div className="flex items-center gap-3"><Users size={18} className="text-accent" /><span className="text-sm">{t("rooms.stat_guests")}</span></div>
                  <div className="flex items-center gap-3"><Bath size={18} className="text-accent" /><span className="text-sm">{t("rooms.stat_bathrooms")}</span></div>
                </div>
                {hovered && (
                  <div className="mt-6 p-4 rounded-xl bg-accent/10 border border-accent/30 animate-fade-up">
                    <div className="overline text-accent">{t("rooms.hovered_label")}</div>
                    <div className="font-serif text-xl mt-1">
                      {{
                        camera: t("room.camera_name"),
                        cameretta: t("room.cameretta_name"),
                        cucina: t("room.cucina_name"),
                        soggiorno: t("room.cucina_name"),
                        bagno: t("room.bagno_name"),
                        disimpegno: t("rooms.stat_surface"),
                      }[hovered]}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Rooms list */}
        <div className="space-y-20">
          {ROOMS.map((r, i) => (
            <Reveal key={r.id} delay={i * 80}>
              <div className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden group">
                  <img src={r.img} alt={r.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div>
                  <div className="overline text-accent mb-4">0{i + 1} · {t("rooms.space_label")}</div>
                  <h3 className="font-serif text-4xl lg:text-5xl mb-5">{r.name}</h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">{r.desc}</p>
                  <div className="flex flex-wrap gap-3">
                    <span className="text-xs px-4 py-2 border border-border rounded-full">{r.beds}</span>
                    {r.guests && <span className="text-xs px-4 py-2 border border-border rounded-full">{r.guests} {t("room.guests_suffix")}</span>}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-24 text-center">
          <Link
            to="/booking"
            data-testid="rooms-cta-book"
            className="btn-shimmer inline-block px-12 py-4 rounded-full bg-foreground text-background hover:bg-accent transition-colors text-sm tracking-wide"
          >
            {t("rooms.cta")}
          </Link>
        </Reveal>
      </div>
    </main>
  );
};

export default RoomsPage;
